import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, MessageCircle } from 'lucide-react';
import { urlImagemServicoFallback } from '../../utils/barbeariaCatalogo';
import {
  agoraISO,
  fmtBrl,
  listarDatasCalendario,
  listarSlotsDisponiveis,
  linkWhatsAppLembrete,
  montarLembretesAgendamento,
  uid,
  verificarConflitoAgenda,
} from '../../utils/barbeariaLogica';
import {
  checarApiNuvemDisponivel,
  publicarAgendamentoNuvem,
  puxarEstadoNuvem,
  mesclarEstadosBarbearia,
} from '../../utils/barbeariaSyncNuvem';
import type { BarbeariaEstado } from '../../utils/barbeariaTipos';

interface Props {
  estado: BarbeariaEstado;
  patch: (fn: (prev: BarbeariaEstado) => BarbeariaEstado) => void;
  /** Link do cliente: esconde extras de loja. */
  modoCliente?: boolean;
  /** Após confirmar no app da loja, abre Agenda/Admin. */
  onConfirmadoNaLoja?: () => void;
  /** Link cliente: pedido realmente enviado — para sync. */
  onPedidoConfirmadoCliente?: () => void;
  /** Link cliente: quer fazer outro agendamento — retoma sync. */
  onNovoPedidoCliente?: () => void;
}

function fmtDuracao(min: number): string {
  const m = Math.max(0, Math.floor(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}min` : `${h}h`;
}

function ImgServico({ imagem, nome }: { imagem?: string; nome: string }) {
  const fontes = urlImagemServicoFallback(imagem);
  const [idx, setIdx] = useState(0);
  if (!fontes.length || idx >= fontes.length) {
    return (
      <div className="flex aspect-[3/4] min-h-[14rem] w-full items-center justify-center bg-neutral-200 text-xs font-bold text-neutral-500 sm:min-h-[16rem]">
        Sem foto
      </div>
    );
  }
  return (
    <div className="flex aspect-[3/4] min-h-[14rem] w-full items-center justify-center bg-neutral-100 p-1 sm:min-h-[16rem]">
      <img
        src={fontes[idx]}
        alt={nome}
        className="h-full w-full object-contain object-center"
        onError={() => setIdx((i) => i + 1)}
      />
    </div>
  );
}

export default function BarbeariaAgendamento({
  estado,
  patch,
  modoCliente = false,
  onConfirmadoNaLoja,
  onPedidoConfirmadoCliente,
  onNovoPedidoCliente,
}: Props) {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes0, setMes0] = useState(hoje.getMonth());
  const [dataISO, setDataISO] = useState(
    `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`
  );
  const [servicoId, setServicoId] = useState(estado.servicos.find((s) => s.ativo)?.id || '');
  const [barbeiroId, setBarbeiroId] = useState(estado.barbeiros.find((b) => b.ativo)?.id || '');
  const [horaInicio, setHoraInicio] = useState('');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [msg, setMsg] = useState('');
  const [enviando, setEnviando] = useState(false);
  /** null = verificando; só no modoCliente. */
  const [apiOk, setApiOk] = useState<boolean | null>(modoCliente ? null : true);
  /** Só true depois de envio real na nuvem (modoCliente). */
  const [pedidoConfirmado, setPedidoConfirmado] = useState(false);
  const [resumoConfirmado, setResumoConfirmado] = useState('');
  const refHorarios = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!modoCliente || pedidoConfirmado) {
      if (!modoCliente) setApiOk(true);
      return;
    }
    let cancelado = false;
    const verificar = async () => {
      const ok = await checarApiNuvemDisponivel();
      if (!cancelado) setApiOk(ok);
    };
    void verificar();
    const id = window.setInterval(() => void verificar(), 25_000);
    return () => {
      cancelado = true;
      window.clearInterval(id);
    };
  }, [modoCliente, pedidoConfirmado]);

  useEffect(() => {
    if (!servicoId && estado.servicos.find((s) => s.ativo)) {
      setServicoId(estado.servicos.find((s) => s.ativo)!.id);
    }
    if (!barbeiroId && estado.barbeiros.find((b) => b.ativo)) {
      setBarbeiroId(estado.barbeiros.find((b) => b.ativo)!.id);
    }
  }, [estado.servicos, estado.barbeiros, servicoId, barbeiroId]);

  const reiniciarParaNovoPedido = () => {
    const agora = new Date();
    setAno(agora.getFullYear());
    setMes0(agora.getMonth());
    setDataISO(
      `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`
    );
    setServicoId(estado.servicos.find((s) => s.ativo)?.id || '');
    setBarbeiroId(estado.barbeiros.find((b) => b.ativo)?.id || '');
    setHoraInicio('');
    setNome('');
    setTelefone('');
    setMsg('');
    setPedidoConfirmado(false);
    setResumoConfirmado('');
    setApiOk(modoCliente ? null : true);
    onNovoPedidoCliente?.();
  };

  const servico = estado.servicos.find((s) => s.id === servicoId && s.ativo)
    || estado.servicos.find((s) => s.id === servicoId);
  const barbeiro = estado.barbeiros.find((b) => b.id === barbeiroId && b.ativo)
    || estado.barbeiros.find((b) => b.id === barbeiroId);

  const cells = useMemo(() => listarDatasCalendario(ano, mes0), [ano, mes0]);
  const slots = useMemo(() => {
    if (!servico || !barbeiroId || !dataISO) return [];
    return listarSlotsDisponiveis(estado, dataISO, barbeiroId, servico.duracaoMin);
  }, [estado, dataISO, barbeiroId, servico]);

  // Se o horário escolhido sumiu da lista (outro cliente pegou), limpa
  useEffect(() => {
    if (horaInicio && slots.length > 0 && !slots.includes(horaInicio)) {
      setHoraInicio('');
    }
  }, [slots, horaInicio]);

  const mesLabel = new Date(ano, mes0, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

  const irParaHorarios = () => {
    window.setTimeout(() => {
      refHorarios.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  const agendar = async () => {
    setMsg('');
    if (!servico) {
      setMsg('Escolha um serviço (toque na foto).');
      return;
    }
    if (!barbeiroId || !barbeiro) {
      setMsg('Escolha o barbeiro.');
      return;
    }
    if (!horaInicio) {
      setMsg('Escolha um horário na lista de horários disponíveis.');
      irParaHorarios();
      return;
    }
    if (!nome.trim() || !telefone.trim()) {
      setMsg('Informe nome e WhatsApp.');
      return;
    }

    if (modoCliente) {
      setEnviando(true);
      const online = await checarApiNuvemDisponivel();
      setApiOk(online);
      if (!online) {
        setEnviando(false);
        setMsg(
          'O sistema de agendamento está temporariamente indisponível. Tente de novo em alguns minutos ou fale com a barbearia pelo WhatsApp.'
        );
        return;
      }
    } else {
      setEnviando(true);
    }

    const remoto = await puxarEstadoNuvem();
    let estadoAgora = estado;
    if (remoto) {
      estadoAgora = mesclarEstadosBarbearia(estado, remoto);
      patch(() => estadoAgora);
    }

    const check = verificarConflitoAgenda({
      estado: estadoAgora,
      barbeiroId,
      dataISO,
      horaInicio,
      duracaoMin: servico.duracaoMin,
    });
    if (!check.ok) {
      setEnviando(false);
      setMsg(
        check.motivo.includes('Já existe')
          ? 'Este horário não está mais disponível. Escolha outro.'
          : check.motivo
      );
      setHoraInicio('');
      irParaHorarios();
      return;
    }

    const agora = agoraISO();
    const telDig = telefone.replace(/\D/g, '');
    const clienteExistente = estadoAgora.clientes.find(
      (c) =>
        c.telefone.replace(/\D/g, '') === telDig ||
        c.nome.trim().toLowerCase() === nome.trim().toLowerCase()
    );
    const clienteSalvo = clienteExistente
      ? {
          ...clienteExistente,
          nome: nome.trim().toUpperCase(),
          telefone: telefone.trim(),
          atualizadoEm: agora,
        }
      : {
          id: uid('cli'),
          nome: nome.trim().toUpperCase(),
          telefone: telefone.trim(),
          notas: '',
          criadoEm: agora,
          atualizadoEm: agora,
        };

    const lembretes = montarLembretesAgendamento(dataISO, horaInicio);
    const agendamentoSalvo = {
      id: uid('agd'),
      clienteId: clienteSalvo.id,
      barbeiroId,
      servicoId: servico.id,
      dataISO,
      horaInicio,
      status: 'confirmado' as const,
      ...lembretes,
      criadoEm: agora,
      atualizadoEm: agora,
    };

    const resultado = await publicarAgendamentoNuvem({
      agendamento: agendamentoSalvo,
      cliente: clienteSalvo,
      duracaoMin: servico.duracaoMin,
    });

    if (!resultado.ok && resultado.conflito) {
      if (resultado.estado) patch((prev) => mesclarEstadosBarbearia(prev, resultado.estado!));
      else if (remoto) patch((prev) => mesclarEstadosBarbearia(prev, remoto));
      setEnviando(false);
      setMsg(resultado.erro || 'Este horário não está mais disponível. Escolha outro.');
      setHoraInicio('');
      irParaHorarios();
      return;
    }

    // Link do cliente: sem nuvem = sem confirmação (não grava só no navegador).
    if (modoCliente && !resultado.ok) {
      setEnviando(false);
      setApiOk(false);
      setMsg(
        resultado.erro?.includes('rede') || resultado.erro?.includes('conexão')
          ? 'Não foi possível enviar: o sistema está offline. Tente de novo em alguns minutos.'
          : resultado.erro ||
              'Não foi possível confirmar o agendamento. Tente de novo ou fale com a barbearia.'
      );
      return;
    }

    patch((prev) => {
      const base = resultado.estado ? mesclarEstadosBarbearia(prev, resultado.estado) : prev;
      const jaTem = base.agendamentos.some((a) => a.id === agendamentoSalvo.id);
      return {
        ...base,
        clientes: base.clientes.some((c) => c.id === clienteSalvo.id)
          ? base.clientes.map((c) => (c.id === clienteSalvo.id ? clienteSalvo : c))
          : [...base.clientes, clienteSalvo],
        agendamentos: jaTem ? base.agendamentos : [...base.agendamentos, agendamentoSalvo],
        atualizadoEm: agora,
      };
    });

    setEnviando(false);

    if (modoCliente) {
      const dataBr = dataISO.split('-').reverse().join('/');
      setResumoConfirmado(
        `${servico.nome} · ${barbeiro?.nome || 'Barbeiro'} · ${dataBr} às ${horaInicio}`
      );
      setMsg('Agendamento confirmado! A barbearia já recebeu na Agenda/Admin.');
      setPedidoConfirmado(true);
      setHoraInicio('');
      onPedidoConfirmadoCliente?.();
      return;
    }

    setMsg(
      resultado.ok
        ? 'Agendamento confirmado! Abrindo Agenda/Admin…'
        : 'Agendamento salvo na loja (nuvem indisponível). Abrindo Agenda/Admin…'
    );
    onConfirmadoNaLoja?.();
    setHoraInicio('');
  };

  const abrirLembreteDemo = () => {
    const link = linkWhatsAppLembrete(
      telefone || '11999999999',
      nome || 'Cliente',
      dataISO,
      horaInicio || '10:00',
      servico?.nome || 'serviço',
      estado.config.nomeLoja
    );
    window.open(link, '_blank');
  };

  const resumoPronto = Boolean(servico && barbeiro && horaInicio);

  if (modoCliente && pedidoConfirmado) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border-2 border-emerald-800 bg-emerald-50 px-4 py-5 text-center shadow-sm">
          <p className="text-base font-bold text-emerald-950">Pedido enviado com sucesso</p>
          {resumoConfirmado && (
            <p className="mt-2 text-sm font-medium text-emerald-900">{resumoConfirmado}</p>
          )}
          <p className="mt-2 text-sm text-emerald-900">
            A barbearia já recebeu na Agenda. Esta página foi encerrada para não ficar consultando
            a nuvem sem necessidade.
          </p>
          <button
            type="button"
            onClick={reiniciarParaNovoPedido}
            className="bb-btn-primary mt-4 px-4 py-2.5"
          >
            Fazer outro pedido
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {modoCliente && apiOk === false && (
        <div className="rounded-xl border-2 border-red-800 bg-red-50 px-3 py-3 text-sm font-medium text-red-950">
          Sistema de agendamento temporariamente indisponível. O envio está bloqueado para não
          perder seu horário. Tente de novo em alguns minutos ou fale com a barbearia pelo WhatsApp.
        </div>
      )}
      {modoCliente && apiOk === null && (
        <div className="rounded-xl border border-neutral-400 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
          Verificando conexão com o agendamento…
        </div>
      )}

      {/* Resumo fixo: o que falta / o que já escolheu */}
      <div className="sticky top-0 z-30 rounded-xl border-2 border-neutral-800 bg-white/95 px-3 py-2 shadow-md backdrop-blur-sm">
        <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">Seu pedido</p>
        <p className="text-sm font-bold text-neutral-950">
          {servico ? servico.nome : '① Serviço'}
          {' · '}
          {barbeiro ? barbeiro.nome : '② Barbeiro'}
          {' · '}
          {horaInicio ? `${dataISO.split('-').reverse().join('/')} ${horaInicio}` : '③ Horário'}
        </p>
        {!resumoPronto && (
          <p className="mt-0.5 text-[11px] font-medium text-amber-800">
            Ordem: serviço → barbeiro → data/horário → nome e WhatsApp → confirmar
          </p>
        )}
      </div>

      {/* 1) Serviço + barbeiro primeiro (no celular isso ficava depois do horário) */}
      <section className="space-y-3 bb-card">
        <h3 className="text-sm font-bold uppercase tracking-wide text-neutral-800">
          {modoCliente ? '1. Escolha o serviço' : 'Catálogo'}
        </h3>

        <div data-tour="agd-servicos">
          <p className="mb-2 text-xs font-medium text-neutral-600">Toque na foto do serviço</p>
          <div className="grid max-h-[36rem] grid-cols-1 gap-3 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
            {estado.servicos
              .filter((s) => s.ativo)
              .map((s) => {
                const ativo = s.id === servicoId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setServicoId(s.id);
                      setHoraInicio('');
                      setMsg('');
                    }}
                    className={`rounded-xl border-2 text-left shadow-sm transition ${
                      ativo
                        ? 'border-amber-800 ring-2 ring-amber-400'
                        : 'border-neutral-400 hover:border-amber-600'
                    }`}
                  >
                    <div className="overflow-hidden rounded-t-[10px]">
                      <ImgServico imagem={s.imagem} nome={s.nome} />
                    </div>
                    <div className="bg-white px-2.5 py-2">
                      <p className="text-xs font-bold leading-tight text-neutral-900 sm:text-sm">
                        {s.nome}
                      </p>
                      <p className="text-xs font-semibold text-amber-800 sm:text-sm">
                        {fmtBrl(s.preco)}
                      </p>
                      <p className="text-[10px] text-neutral-500">{fmtDuracao(s.duracaoMin)}</p>
                    </div>
                  </button>
                );
              })}
          </div>
          {servico && (
            <p className="mt-2 rounded-lg border border-amber-700 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-950">
              Selecionado: {servico.nome} — {fmtBrl(servico.preco)} · {fmtDuracao(servico.duracaoMin)}
            </p>
          )}
        </div>

        <div data-tour="agd-barbeiro">
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-neutral-700">
            2. Barbeiro
          </p>
          <div className="grid grid-cols-2 gap-2">
            {estado.barbeiros
              .filter((b) => b.ativo)
              .map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => {
                    setBarbeiroId(b.id);
                    setHoraInicio('');
                    setMsg('');
                    irParaHorarios();
                  }}
                  className={`rounded-xl border-2 px-3 py-3 text-sm font-bold ${
                    barbeiroId === b.id
                      ? 'border-amber-900 bg-amber-600 text-white'
                      : 'border-neutral-500 bg-neutral-100 text-neutral-900 hover:bg-amber-50'
                  }`}
                >
                  {b.nome}
                </button>
              ))}
          </div>
        </div>
      </section>

      {/* 2) Calendário + horários */}
      <section className="bb-card" data-tour="agd-calendario">
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-neutral-800">
          3. Data e horário
        </h3>
        <div className="mb-3 flex items-center justify-between gap-2">
          <button
            type="button"
            className="bb-btn-ghost px-2 py-1 text-sm"
            onClick={() => {
              if (mes0 === 0) {
                setAno((y) => y - 1);
                setMes0(11);
              } else setMes0((m) => m - 1);
            }}
          >
            ‹
          </button>
          <span className="text-sm font-semibold capitalize">{mesLabel}</span>
          <button
            type="button"
            className="bb-btn-ghost px-2 py-1 text-sm"
            onClick={() => {
              if (mes0 === 11) {
                setAno((y) => y + 1);
                setMes0(0);
              } else setMes0((m) => m + 1);
            }}
          >
            ›
          </button>
        </div>
        <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-neutral-500">
          {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`e-${i}`} className="h-9" />;
            const selecionado = iso === dataISO;
            const temAgenda = estado.agendamentos.some(
              (a) => a.dataISO === iso && a.status !== 'cancelado'
            );
            return (
              <button
                key={iso}
                type="button"
                onClick={() => {
                  setDataISO(iso);
                  setHoraInicio('');
                  setMsg('');
                }}
                className={`h-9 rounded-lg text-sm font-semibold transition ${
                  selecionado
                    ? 'border-2 border-amber-900 bg-amber-600 text-white shadow'
                    : temAgenda
                      ? 'border-2 border-amber-700 bg-amber-100 text-amber-950 hover:bg-amber-200'
                      : 'border-2 border-neutral-400 bg-white text-neutral-800 hover:bg-neutral-100'
                }`}
              >
                {Number(iso.slice(-2))}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-neutral-500">
          Data: <strong>{dataISO.split('-').reverse().join('/')}</strong>
          {servico ? (
            <>
              {' '}
              · duração: <strong>{fmtDuracao(servico.duracaoMin)}</strong>
            </>
          ) : null}
        </p>

        <div
          className="mt-4 border-t-2 border-neutral-200 pt-3"
          data-tour="agd-horarios"
          ref={refHorarios}
        >
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutral-700">
            Horários disponíveis — toque para escolher
          </p>
          <div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
            {!servico || !barbeiroId ? (
              <span className="text-xs text-neutral-500">
                Escolha o serviço e o barbeiro acima para ver os horários.
              </span>
            ) : slots.length === 0 ? (
              <span className="text-xs font-medium text-amber-800">
                Nenhum horário livre neste dia. Escolha outra data.
              </span>
            ) : (
              slots.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => {
                    setHoraInicio(h);
                    setMsg('');
                  }}
                  className={`rounded-md border-2 px-2.5 py-1.5 text-xs font-bold shadow-sm ${
                    horaInicio === h
                      ? 'border-amber-900 bg-amber-600 text-white'
                      : 'border-neutral-500 bg-neutral-100 text-neutral-900 hover:border-amber-700 hover:bg-amber-50'
                  }`}
                >
                  {h}
                </button>
              ))
            )}
          </div>
          {horaInicio && (
            <p className="mt-2 text-sm font-bold text-emerald-800">
              Horário escolhido: {horaInicio}
            </p>
          )}
        </div>
      </section>

      {/* 3) Dados + confirmar */}
      <section className="space-y-3 bb-card">
        <h3 className="text-sm font-bold uppercase tracking-wide text-neutral-800">
          4. Seus dados
        </h3>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block text-xs font-medium text-neutral-600">
            Nome
            <input
              className="bb-input uppercase"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Seu nome"
            />
          </label>
          <label className="block text-xs font-medium text-neutral-600">
            WhatsApp
            <input
              className="bb-input"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="11999998888"
              inputMode="tel"
            />
          </label>
        </div>

        {!modoCliente && (
          <p className="flex items-center gap-2 text-xs text-neutral-700">
            <Bell className="h-3.5 w-3.5 text-amber-700" />
            Lembretes WhatsApp automáticos: <strong>1 dia</strong> e <strong>1 hora</strong> antes
            (chegar 10 min antes). O app da loja avisa na hora de enviar.
          </p>
        )}

        <div className="flex flex-wrap gap-2" data-tour="agd-confirmar">
          <button
            type="button"
            onClick={() => void agendar()}
            disabled={enviando || (modoCliente && apiOk !== true)}
            className="bb-btn-primary px-4 py-2.5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {enviando
              ? 'Enviando…'
              : modoCliente && apiOk === false
                ? 'Envio bloqueado (sistema offline)'
                : modoCliente && apiOk === null
                  ? 'Aguarde…'
                  : 'Confirmar agendamento'}
          </button>
          {!modoCliente && (
            <button
              type="button"
              onClick={abrirLembreteDemo}
              className="bb-btn-ghost border-emerald-700 bg-emerald-100 text-emerald-950"
              title="Abrir WhatsApp com mensagem de lembrete"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              Testar msg WhatsApp
            </button>
          )}
        </div>
        {msg && (
          <p
            className={`rounded-xl border px-3 py-2 text-sm font-medium ${
              msg.includes('confirmado')
                ? 'border-emerald-700 bg-emerald-50 text-emerald-950'
                : 'border-amber-700 bg-amber-50 text-amber-950'
            }`}
          >
            {msg}
          </p>
        )}
      </section>
    </div>
  );
}
